import { Device } from "./Device";
import { List } from "../Utils/List";
import { IpAddress, IpVersion } from "./IpAddress";
import { Dictionary } from "../Utils/Dictionary";
import { HashSet } from "../Utils/HashSet";

export class IpToDeviceEntry {
	public IpAddress: IpAddress;

	public Devices: List<Device>;
	public get HasDevices(): boolean {
		return this.Devices && this.Devices.Count > 0;
	}

	constructor(ipAddress: IpAddress, device?: Device) {
		this.IpAddress = ipAddress;
		this.Devices = new List<Device>();
		if (device)
			this.Devices.Add(device);
	}

	public AddDevice(device: Device): void {
		if (!this.Devices.Contains(device))
			this.Devices.Add(device);
	}

	public RemoveDevice(device: Device): boolean {
		return (this.Devices.Remove(device));
	}

	public HasSnmpDevice(): boolean {
		return this.Devices.Some((device) => {
			return device.IsSnmpDevice();
		});
	}

	public DuplicateDevices(srcDevice: Device): List<Device> {
		var duplicateDevices = new List<Device>();

		this.Devices.ForEach((device) => {
			if (srcDevice.IsDuplicateDevice(device))
				duplicateDevices.Add(device);
		});

		return (duplicateDevices);
	}
}

export class IpToDeviceTable {
	private _ipTable: Dictionary<IpAddress, IpToDeviceEntry>;

	constructor() {
		this._ipTable = new Dictionary<IpAddress, IpToDeviceEntry>();
	}

	public get IpAddresses(): HashSet<IpAddress> {
		return new HashSet<IpAddress>(this._ipTable.Keys());
	}

	public Add(ipAddress: IpAddress, device: Device): IpToDeviceEntry {
		if ((ipAddress == null) || ipAddress.IsNull)
			return null;

		var ipEntry: IpToDeviceEntry = this._ipTable.Item(ipAddress);
		if (ipEntry) {
			if (!ipEntry.Devices.Contains(device))
				ipEntry.Devices.Add(device);
		} else {
			ipEntry = new IpToDeviceEntry(ipAddress, device);
			this._ipTable.Add(ipAddress, ipEntry);
		}

		return (ipEntry);
	}

	public AddDevice(device: Device): void {
		// REGISTER ALL known IP ADDRESSES on the device
		device.IpAddresses.ForEach((ipAddress) => {
			this.Add(ipAddress, device);
		});
	}

	public Clear(): void {
		this._ipTable.Clear();
	}

	public get Count(): number {
		return (this._ipTable.Count);
	}

	public Find(ipAddress: IpAddress): IpToDeviceEntry {
		if (!ipAddress || ipAddress.IsNull)
			return null;

		return this._ipTable.Item(ipAddress);
	}

	private Remove(ipAddress: IpAddress, device: Device): boolean {
		if (!ipAddress || ipAddress.IsNull)
			return false;

		var entryRemoved = false;

		var ipEntry: IpToDeviceEntry = this._ipTable.Item(ipAddress);

		if (ipEntry) {
			// REMOVE binding of MAC-to-DEVICE
			entryRemoved = ipEntry.RemoveDevice(device);

			// CLEAN-UP
			if (!ipEntry.HasDevices) {
				this._ipTable.Remove(ipAddress);
			}
		}

		return (entryRemoved);
	}

	public RemoveDevice(device: Device): boolean {
		if (device == null)
			return false;

		var bindingsRemoved = false;

		// UN-REGISTER ALL known IP ADDRESSES on the device
		device.IpAddresses.ForEach((ipAddress) => {
			if (this.Remove(ipAddress, device))
				bindingsRemoved = true;
		});

		return (bindingsRemoved);
	}

	public GetDuplicateIpAddresses(): List<IpToDeviceEntry> {
		var duplicateIpAddresses = new List<IpToDeviceEntry>();

		this._ipTable.ForEach((pair) => {
			if (pair.Value.Devices.Count > 0)
				duplicateIpAddresses.Add(pair.Value);
		});

		return (duplicateIpAddresses);
	}

	public ForEach(callbackfn: (value: IpToDeviceEntry, index: number, array: IpToDeviceEntry[]) => void, thisArg?: any): void {
		this._ipTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: IpToDeviceEntry, index: number, array: IpToDeviceEntry[]) => boolean, thisArg?: any): boolean {
		return this._ipTable.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: IpToDeviceEntry, index: number, array: IpToDeviceEntry[]) => boolean, thisArg?: any): boolean {
		return this._ipTable.Values().Every(callbackfn, thisArg);
	}
}
